/**
 * Services de structure de contenu pour la transformation de manuscrits
 * Version modernisée du code legacy
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { convertHtmlToBBCode,beautifyBBCode } from './bbcodeHelpers.js';
import { decodeHTMLEntities } from './htmlHelpers.js';
import { get } from '../utils/api-router.js';

/**
 * Convertit un manuscrit en format JSON structuré
 * @param {string} theContent - Contenu HTML du manuscrit
 * @param {string} theContentTag - Tag du manuscrit pour récupérer la configuration
 * @param {string} ia_language - Langue de travail IA (optionnel)
 * @returns {Object} - Structure JSON du manuscrit
 */
async function manuscript2JSON(theContent, theContentTag, ia_language = 'FR') {
    // Récupération des mappings depuis l'API
    const response = await get(`/api/config/read/content-structure?manuscript_tag=${encodeURIComponent(theContentTag)}&ia_language=${encodeURIComponent(ia_language)}`, {}, false, null);
    
    /**
     *     Exemple de réponse de l'API
     * {
     *      success: true,
     *      "content-structure": [ {
     *              cut_level: row.cut_level,
     *              header_text: row.header_text,
     *              clean_content: Boolean(row.clean_content),
     *              article_field: row.article_field,
     *              field_type: row.field_type,
     *              max_size: row.max_size
     *           } ]
     */

    // Vérification de la réponse
    if (!response) {
        debug.log(config, `ERREUR: Réponse API manquante pour la structure de contenu ${theContentTag}`);
        return null;
    } else if (!response['content-structure']) {
        debug.log(config, `ERREUR: Propriété 'content-structure' manquante dans les données de l'API ${theContentTag}`, response);
        return null;    
    } else if (Array.isArray(response['content-structure']) && response['content-structure'].length === 0) {
        debug.log(config, `AVERTISSEMENT: La structure de contenu est un tableau vide ${theContentTag}`, response);
        return null;
    }

    // Adaptation de la structure retournée par l'API
    const GetManuscript2Article = response['content-structure'] || [];

    debug.log(config, "Structure de contenu récupérée", GetManuscript2Article);
    
    // Découpage du contenu en sections selon le niveau de titre
    let theItems = [];
    
    // Vérification que la structure contient des données
    if (GetManuscript2Article.length === 0) {
        debug.log(config, "Aucune structure de contenu trouvée, utilisation du niveau de titre par défaut (2)");
        theItems = theContent.replace(/<h([12])>/gi, '<h$1>SLICE|$1').split(/<\/?h[12]>/i);
    } else {
        const cut_level = GetManuscript2Article[0].cut_level.toString();
        debug.log(config, "Niveau de découpage utilisé", cut_level);
        
        switch(cut_level) {
        case "1":
            theItems = theContent.replace(/<h1>/gi, '<h1>SLICE|1').split(/<\/?h1>/i);
            break;
        case "2":
            theItems = theContent.replace(/<h([12])>/gi, '<h$1>SLICE|$1').split(/<\/?h[12]>/i);
            break;
        case "3":
            theItems = theContent.replace(/<h([123])>/gi, '<h$1>SLICE|$1').split(/<\/?h[123]>/i);
            break;
        case "4":
            theItems = theContent.replace(/<h([1234])>/gi, '<h$1>SLICE|$1').split(/<\/?h[1234]>/i);
            break;				
        }
    }
    debug.log(config, "manuscript2JSON : Sections découpées", theItems);

    // détermine le type de structure
    const hasArticleMapping = GetManuscript2Article.some(item => item.article_field && item.article_field !== "none");

    // traitement des sections
    const stringData = {};

    // Variables de traitement
    let theSubContent = "";
    let theSubContentNormalizedHeader = "none-0";
    let theSubContentHeader = "";
    let theisContent = false;
    let theMapping = null;
    let theHeaderLevel = "0";
    let theclean_content = false;
    let theType = "formated";
    let theMaxSize = 0;

    // Parcourir toutes les sections
    for(let i = 0; i < theItems.length; i++) {
        if (theItems[i].startsWith('SLICE|')) {
            // Sauvegarder la section précédente
            if ((theSubContentNormalizedHeader !== "none-0") || (theSubContent > "")) {
                stringData[theSubContentNormalizedHeader] = { 
                    mapping: theMapping,
                    headerlevel: theHeaderLevel,
                    header: theSubContentHeader,
                    content: (stringData[theSubContentNormalizedHeader] ? stringData[theSubContentNormalizedHeader].content : "") + theSubContent,
                    clean_content: theclean_content,
                    type: theType,
                    maxsize: theMaxSize,
                    isContent : theisContent
                };
            }
            
            // Définition de la nouvelle section
            theSubContentHeader = decodeHTMLEntities((theItems[i].split(/\|/)[1]).replace(/<span.+span>/, '')).trim();
            theMapping = hasArticleMapping ? "none" : null;
            theisContent = false;
            theSubContentNormalizedHeader = "none-" + i.toString();
            theclean_content = false;
            theType = "formated";
            theMaxSize = 0;
            
            // Rechercher le mapping pour cette section
            for (let j = 0; j < GetManuscript2Article.length; j++) {
                if (theSubContentHeader.toLowerCase().substring(1).trim() === (GetManuscript2Article[j].header_text.toLowerCase())) {
                    theMapping = GetManuscript2Article[j].article_field;
                    theSubContentNormalizedHeader = GetManuscript2Article[j].header_text;
                    theclean_content = (GetManuscript2Article[j].clean_content === 1);
                    theType = GetManuscript2Article[j].field_type;
                    theMaxSize = (GetManuscript2Article[j].max_size === "" ? 0 : GetManuscript2Article[j].max_size);
                    theisContent = true;
                    break;
                } 
            }
            
            theHeaderLevel = theSubContentHeader.charAt(0);
            theSubContentHeader = theSubContentHeader.slice(1);
            theSubContent = "";
        } else {
            theSubContent = theSubContent + theItems[i];
        }
    }
    
    // Sauvegarder la dernière section
    if ((theSubContentNormalizedHeader !== "none-0") || (theSubContent > "")) {
        stringData[theSubContentNormalizedHeader] = { 
            mapping: theMapping,
            headerlevel: theHeaderLevel,
            header: theSubContentHeader,
            content: (stringData[theSubContentNormalizedHeader] ? stringData[theSubContentNormalizedHeader].content : "") + theSubContent,
            clean_content: theclean_content,
            type: theType,
            maxsize: theMaxSize,
            isContent : theisContent
        };
    }

    // Traitement final des sections
    const keyfound = [];
    for(const key in stringData) {
        if (stringData[key].type === 'text') {
            // Nettoyage des balises <p> pour le texte
            stringData[key].content = stringData[key].content.replace(/^\s*<p>\s*|\s*<\/p>\s*$/g, '').replace(/<p>\s*<\/p>/g, '<br>');			
        } else {
            // Nettoyage des espaces pour les autres types
            stringData[key].content = stringData[key].content.replace(/^\s+|\s+$/g, '');
        }
        
        // Limitation de la taille si nécessaire
        if (stringData[key].maxsize > 0) {
            stringData[key].content = stringData[key].content.substring(0, stringData[key].maxsize);
        }
        keyfound.push(key);			
    }

    // Ajout des sections manquantes (définies dans la configuration mais absentes du manuscrit)
    for (let j = 0; j < GetManuscript2Article.length; j++) {
        if (!keyfound.includes(GetManuscript2Article[j].header_text)) {
            stringData[GetManuscript2Article[j].header_text] = { 
                mapping: GetManuscript2Article[j].article_field,
                headerlevel: GetManuscript2Article[j].cut_level,
                header: GetManuscript2Article[j].header_text,
                content: '',
                clean_content: GetManuscript2Article[j].clean_content,
                type: GetManuscript2Article[j].field_type,
                maxsize: GetManuscript2Article[j].max_size,
                isContent : true
            };
        }
    }

    debug.log(config, `manuscript2JSON : Manuscript JSON structure ${theContentTag}`, stringData);  
    return stringData;
}

/**
 * Convertit une structure JSON de manuscrit en manuscrit HTML
 * @param {Object} theData - Structure JSON du manuscrit
 * @returns {string} - Manuscrit HTML avec indication des mappings
 */
async function json2Manuscript(theData) {
    let outputData = "";
    for(const key in theData) {
        if ((theData[key].isContent === true) || (theData[key].mapping !== null && theData[key].mapping !== "")) {
            outputData += '<h' + theData[key].headerlevel + '>';
            if (theData[key].mapping !== null) {
                outputData += (theData[key].mapping === "none" ? 
                '<span style="background-color: #e03e2d; color: #ecf0f1; font-size: 12pt;">' : 
                '<span style="background-color: #169179; color: #ecf0f1; font-size: 12pt;">') + 
                '&nbsp;' + theData[key].mapping + ' </span>&nbsp;';
            }
            outputData += theData[key].header + '</h' + theData[key].headerlevel + '>';
            if (theData[key].clean_content) {
                outputData += theData[key].content.replace(/<h2>.+?<\/h2>/gi, '')
                    .replace(/<h3>.+?<\/h3>/gi, '')
                    .replace(/<h4>.+?<\/h4>/gi, '');
            } else {
                outputData += theData[key].content;
            }
        }
    }
    debug.log(config, "json2Manuscript : After article structure identification", outputData);  
    return outputData;
}

/**
 * Convertit une structure JSON de manuscrit en structure d'article
 * @param {Object} theData - Structure JSON du manuscrit
 * @returns {Object} - Objet JSON structuré pour un article
 */
async function json2Article(theData) {
    let theJSON = {};
    let theText;

    for(const key in theData) {
        if (theData[key].mapping !== "none") {
            if (theData[key].content > "") {
                debug.log(config, `json2Article : Traitement mapping "${theData[key].mapping}","${theData[key].type}","${theData[key].max_size}" pour la section "${key}"`, {
                    contentLength: theData[key].content.length,
                    type: theData[key].type
                });
                
                // Traitement selon le type de champ
                if (theData[key].type === "text") {
                    // Type texte : conversion HTML vers texte sans beautify
                    const decoded = decodeHTMLEntities(theData[key].content.replace(/<[^>]+>/gi, ''));
                    // Nettoyage des caractères de contrôle et espaces spéciaux
                    theText = decoded.replace(/[\p{C}\p{Z}]+/gu, ' ').trim();
                } else {
                    // Type formaté : conversion HTML vers BBCode avec beautify
                    theText = await convertHtmlToBBCode(decodeHTMLEntities(theData[key].content));
                    theText = beautifyBBCode(theText);
                    // Nettoyage des caractères de contrôle et espaces spéciaux
                    theText = theText.replace(/[\p{C}\p{Z}]+/gu, ' ').trim();
                }
                
                // Application de la limite de taille si définie
                if (theData[key].maxsize && theData[key].maxsize > 0) {
                    theText = theText.substring(0, theData[key].maxsize);
                }
                
                theJSON[theData[key].mapping] = theText;
            }
        }
    }
    debug.log(config, "json2Article : Article JSON", theJSON);
    return theJSON;
}

async function text2Article(theData) {
    let theJSON = {};

    const convertedText = await convertHtmlToBBCode(decodeHTMLEntities(theData));
    theJSON['content'] = beautifyBBCode(convertedText);

    debug.log(config, "text2Article : Article JSON", theJSON);
    return theJSON;
}


// Exportation des fonctions
export {
    manuscript2JSON,
    json2Manuscript,
    json2Article,
    text2Article
};
